import { Box, createStyles } from '@mantine/core';
import type { Filter } from '@repo/api-codegen';
import { EntityType } from '@repo/common/enums/entityType';
import { useMemo } from 'react';
import { teamsGrantedFilter, useSchemaList } from '../../../api';
import { useDatabaseList } from '../../../api/hooks/database';
import { useCatalogList } from '../../../api/hooks/resourceCatalog';
import { useCatalogFilter } from '../../../components/Filter/useCatalogFilter';
import { TableV2 } from '../../../components/TableV2';
import { useDataQualityScoreColumns } from '../../../components/TableV2/SecodaEntity.hooks';
import { useEntityBreadcrumbTeam } from '../../../hooks/useEntityBreadcrumbs';
import { useFeatureFlags } from '../../../utils/featureFlags';
import {
	useActions,
	useColumns,
} from '../../TeamCatalogPage/TeamCatalogPage.hooks';

interface ITablesTabProps {
	databaseId: string;
	integrationId: string;
}

const useStyles = createStyles({
	tableWrapper: {
		height: '100%',
	},
});

const COLUMN_VISIBILITY = {
	catalogType: EntityType.table,
	catalogServerType: EntityType.table,
} as const;

const DEFAULT_SEARCH_PARAMS = {
	entity_type: EntityType.table,
} as const;

function TablesTab({ databaseId, integrationId }: ITablesTabProps) {
	const { classes } = useStyles();

	const defaultColumns = useColumns();
	const { teamsPermissionsV2Frontend } = useFeatureFlags();
	const { teamBreadcrumb } = useEntityBreadcrumbTeam();
	const dataQualityColumns = useDataQualityScoreColumns({
		catalogType: EntityType.table,
	});

	const columns = useMemo(
		() => [...defaultColumns, ...dataQualityColumns],
		[dataQualityColumns, defaultColumns]
	);

	const { data: databaseList, isLoading: databaseListLoading } =
		useDatabaseList({
			filters: {
				integration_id: integrationId,
			},
		});

	const { data: schemaList, isLoading: schemaListLoading } = useSchemaList({
		filters: {
			parent_id: databaseId,
		},
	});

	const actions = useActions();

	const filterOptions = useCatalogFilter();

	const defaultCatalogFilters = useMemo(() => {
		const operands: Filter[] = [];

		// If this is the only database on the integration,
		// we should filter by `integration_id` as it will be faster.
		if (databaseList?.count === 1) {
			operands.push({
				operands: [],
				field: 'integration_id',
				operator: 'exact',
				value: integrationId,
			});
			// If there are multiple databases on the integration, we should filter by
			// the schemas. This is slower and a less common case.
		} else {
			operands.push({
				operands: [],
				field: 'parent_id',
				operator: 'in',
				value: schemaList?.results.map((schema) => schema.id),
			});
		}

		if (teamBreadcrumb && teamsPermissionsV2Frontend) {
			operands.push(teamsGrantedFilter(teamBreadcrumb));
		}

		return { operands };
	}, [
		databaseList,
		integrationId,
		schemaList,
		teamsPermissionsV2Frontend,
		teamBreadcrumb,
	]);

	if (databaseListLoading || schemaListLoading) {
		return null;
	}

	return (
		<Box className={classes.tableWrapper}>
			<TableV2
				pluralTypeString="tables"
				withSearch
				withInteractiveHeader
				withServerCsvExport
				withCsvExport
				withCheckbox
				withActions={actions}
				columns={columns}
				withFilterOptions={filterOptions}
				usePaginationList={useCatalogList}
				columnVisibility={COLUMN_VISIBILITY}
				defaultRequiredSearchParams={DEFAULT_SEARCH_PARAMS}
				defaultRequiredCatalogFilters={defaultCatalogFilters}
			/>
		</Box>
	);
}

export default TablesTab;
